import { toHexFromId, wrapApiCall } from '#~/api/index.js';
import { withTransientRetry } from '#~/api/transient-retry.js';
import { config } from '#~/config/index.js';
import { OfflineError, StudioError } from '#~/error/index.js';
import { nats } from '#~/nats/index.js';
import type { User as Auth0User } from '@auth0/auth0-react';
import { UTC } from '@studio/js-utils/time';
import { normalizeId, type Identifier } from '@studio/models';
import type { User } from '@studio/models/user';
import { Role, toRole } from '@studio/models/user';
import type { T_Maybe } from '@tryiris-ai/js-types';
import { STR_NULL, tokenize, toString, toStringArray, transformArray } from '@tryiris-ai/js-types';
import {
  UserRegistryGetUser,
  UserRegistrySendVerificationEmail,
  UserRegistryUpdateUser
} from '@tryiris-ai/mailroom-helper/userRegistry';
import { UserRegistryUpdateUserRequest } from '@tryiris-ai/common.protojs/api/v1/user_registry';
import type { Opaque } from 'type-fest';
import { track } from '#~/analytics/track.js';
import { EVENT_CATEGORIES } from '#~/analytics/constants/event-categories.js';
import { EVENT_NAMES } from '#~/analytics/constants/event-names.js';
import { getSurface } from './email-verification-gate/logic.js';
import { toUserProvider, type UserProvider } from './user-provider.js';

/** Organization data extracted from Auth0 user. */
export interface Auth0OrganizationData {
  /** Organization name. */
  name?: string;
  /** Organization industry. */
  industry?: string;
  /** Organization website. */
  website?: string;
}

/**
 * Module-level per-session dedupe guard for the verification email. The
 * auto-send in `fetchStudioUser` and a user-initiated resend (the
 * EmailVerificationGate, ENG-3797) MUST share this single guard so they cannot
 * double-fire — per spec-fragment.md §7 ("W2 must not introduce a second
 * independent dedupe"). Pending requests are separate from successful sends
 * so a failed request can be retried without claiming a link was sent.
 */
let emailVerificationSentForEmail: string | undefined;
let emailVerificationSentTime: VerificationEmailSentTime | undefined;
const pendingVerificationEmails = new Map<string, Promise<void>>();

/** When the last accepted verification send completed in this page. */
export interface VerificationEmailSentTime {
  /** `performance.now()` time, for cooldowns that system clock changes cannot skew. */
  mono: number;
}

/**
 * Every deliberate send must reach the server, which alone decides the outcome.
 * The requester's default 10 s response cache would replay an earlier success or
 * failure as the answer to a new send (the createRoom mutation opts out the same way).
 */
const verificationEmailRequestOptions = { cacheTTL: 0, staleOnError: false };

/**
 * Mark the verification email as sent for `email` after a successful request.
 */
export const markVerificationEmailSent = (email: string): void => {
  emailVerificationSentForEmail = email;
  emailVerificationSentTime = { mono: performance.now() };
};

/**
 * When the verification email was last accepted for exactly `email` in this page,
 * if it was. Page memory only: reload, logout and sign-in clear it.
 */
export const getVerificationEmailSentAt = (email: string): VerificationEmailSentTime | undefined =>
  emailVerificationSentForEmail === email ? emailVerificationSentTime : undefined;

/**
 * The email the verification message was last sent to this session, if any.
 * The gate reads this on mount to distinguish a sent link from a failed send.
 */
export const getVerificationEmailSentFor = (): string | undefined => emailVerificationSentForEmail;

/**
 * Send (or resend) the verification email and arm the shared dedupe guard.
 * Used by the boot auto-send, the gate, the per-action prompt and Account
 * Settings. With the request cache off, the pending map below is the only thing
 * merging concurrent sends, keyed by the exact address string. The server is
 * authoritative on rate limiting (spec §7) — callers must tolerate rejection.
 */
export const resendVerificationEmail = (params: {
  id: Uint8Array;
  token: string;
  email: string;
}): Promise<void> => {
  const pending = pendingVerificationEmails.get(params.email);
  if (pending) {
    return pending;
  }

  const request = (async (): Promise<void> => {
    await wrapApiCall(async () =>
      UserRegistrySendVerificationEmail.request(
        nats.cloudConnection(),
        { id: params.id, token: params.token },
        verificationEmailRequestOptions
      )
    );
    markVerificationEmailSent(params.email);
  })().finally(() => {
    // Per settle-every-terminal-branch (ai.knowledge/global): release failed
    // requests too, so the shared auto/manual dedupe never prevents recovery.
    pendingVerificationEmails.delete(params.email);
  });
  pendingVerificationEmails.set(params.email, request);
  return request;
};

/**
 * Change the user's email. The registry update nulls `EmailVerifiedAt` and
 * `EmailVerificationSentAt` server-side (spec §8 / update/main.go:58), so the
 * caller should log the user out to re-enter the verify loop cleanly.
 */
export const changeUserEmail = async (params: {
  id: Uint8Array;
  email: string;
  token: string;
}): Promise<void> => {
  const request = UserRegistryUpdateUserRequest.fromPartial({
    id: params.id,
    email: params.email,
    token: params.token
  });

  await wrapApiCall(async () => UserRegistryUpdateUser.request(nats.cloudConnection(), request));
};

/**
 * Generates an authorization failure error. The error codes used here are have no meaning, per se.
 * They are used simply to track where the exception was generated in order to help us debug
 * end-user issues.
 *
 * @param errorCode - Random error code.
 * @param cause - Underlying cause of the exception.
 * @returns Authorization error.
 */
export const fail = (errorCode: string, cause?: Error): Error => {
  // for build comment
  if (!navigator.onLine) {
    return new OfflineError({ cause });
  }

  return new StudioError(`Authorization failed [ERR-${errorCode}]`, { cause });
};

/** Result of fetchStudioUser — includes whether the API fell back to Auth0-only data. */
export interface FetchStudioUserResult {
  user: T_Maybe<User & { organizationData?: Auth0OrganizationData; provider?: UserProvider }>;
  didFallback: boolean;
}

/**
 * Convert the specified jwt token to a `User` record.
 *
 * @param auth0User - Auth0 user record.
 * @param token - JWT token.
 * @returns User record and whether the Studio API was unreachable (fell back to Auth0 data).
 */
export const fetchStudioUser = async (
  auth0User: Auth0User,
  token: string
): Promise<FetchStudioUserResult> => {
  // Try to fetch user from registry via NATS connection
  // If NATS is not connected or fails, fall back to Auth0-only user database
  // This is mainly so that Safari doesn't crap the bed...
  try {
    // Boot-critical read: a transient ITG blip (Failed-to-fetch / 5xx, e.g. a
    // user-registry Lambda COLD START) on this fetch used to fall straight into
    // the catch below → didFallback:true → the synchronizer's B2 "Unable to
    // connect" boot guard on Electron (flaky e2e failure, ENG-4250). Wrap it in
    // the shared bounded transient retry so a cold-start blip is absorbed within
    // the boot budget instead of dead-ending the boot. A 403 (cross-account) is
    // NOT transient → it still falls through to the fallback immediately, so the
    // ENG-4154 cross-account contract is unchanged. Mutations are never wrapped;
    // this is an idempotent GET (users.get).
    // Per rule: reference-rules-in-comments + ai-code-requires-tests (ai.knowledge/global).
    const user = await withTransientRetry(async () =>
      wrapApiCall(async () =>
        UserRegistryGetUser.request(nats.cloudConnection(), {
          id: new Uint8Array([]),
          token
        })
      )
    );

    const id = <Opaque<Identifier, '__userId'>>(<unknown>toHexFromId(user.id)!);

    const name = tokenize(toString(user.name));
    const role = toRole(toStringArray(auth0User['iris/roles']).join(' ')) ?? Role.Operator;
    const timezone = toString(auth0User.tz, { default: UTC });

    const [firstName] = name;
    const lastName = name.slice(1).join(' ');

    const { email, mfaEnabled, profilePhotoUrl } = user;

    if (!email) {
      return { user: undefined, didFallback: false };
    }

    if (
      !user.emailVerified &&
      !user.emailVerificationSent &&
      emailVerificationSentForEmail !== email &&
      !pendingVerificationEmails.has(email)
    ) {
      // Auto-send shares pending requests with manual sends, but only a
      // successful request may update the sent record or success analytics.
      void resendVerificationEmail({ id: user.id, token, email })
        .then(() => {
          void track({
            category: EVENT_CATEGORIES.ONBOARDING,
            name: EVENT_NAMES.VERIFICATION_EMAIL_SENT,
            properties: { surface: getSurface() }
          });
        })
        .catch(() => {
          // Boot continues; the user can retry from the verification prompt.
          console.warn('Unable to send verification email.');
        });
    }

    // Extract organization data from Auth0 metadata
    const organizationData = extractOrganizationData(auth0User);

    // ENG-3451: carry the registry's persisted Auth0 identity provider onto
    // the Studio user so Account Settings can hide password/MFA controls for
    // federated (Google) accounts. toUserProvider passes ONLY the recognized
    // values through — empty/unrecognized stays absent (unknown), and the
    // Auth0-only fallback path below never sets it, so controls are hidden
    // only on a verified registry claim, never on a guess.
    const provider = toUserProvider(user.provider);

    return {
      user: {
        email,
        emailVerified: user.emailVerified,
        // Server-derived gate signal (ENG-4602). Mapped from the GetUser
        // response so the email-verification gate reads it off the depot user.
        verificationRequired: user.verificationRequired,
        firstName: firstName ?? email,
        id,
        lastName,
        role,
        profilePhotoUrl,
        mfaEnabled,
        timezone,
        ...(provider && { provider }),
        ...(organizationData && { organizationData })
      },
      didFallback: false
    };
  } catch (error) {
    // NATS connection not available or user registry call failed
    // Fall back to creating user from Auth0 data only
    console.warn('Unable to fetch user from registry, using Auth0 data only:', error);

    // ENG-3451: only a database login's sub carries the registry id
    // ('auth0|' + hex id). A Google login's registry id is a server-side hash
    // of the sub and creation time (GenProviderID), so an Auth0-only user
    // would publish an id no other surface knows. No user until the registry
    // answers.
    if (!toString(auth0User.sub)?.startsWith('auth0|')) {
      return { user: undefined, didFallback: true };
    }

    const fallbackUser = toStudioUser(auth0User);
    if (!fallbackUser) {
      return { user: undefined, didFallback: true };
    }

    // Extract organization data from Auth0 metadata
    const organizationData = extractOrganizationData(auth0User);

    return {
      user: {
        ...fallbackUser,
        ...(organizationData && { organizationData })
      },
      didFallback: true
    };
  }
};

const reduce = (...args: T_Maybe<string>[]): string =>
  transformArray(args, {
    transformer: toString
  })[0] ?? STR_NULL;

// ENG-4154: like `reduce`, but absent stays undefined — the STR_NULL default
// turned a missing last name into the literal string 'null', which leaked
// into bootstrap room names ("<firstName> null").
const reduceOptional = (...args: T_Maybe<string>[]): T_Maybe<string> =>
  transformArray(args, {
    transformer: toString
  })[0];

/**
 * Convert the specified auth0 user record to a `User` record.
 * This is a lightweight synchronous version used as a fallback when the full user data cannot be fetched.
 *
 * IMPORTANT: This function strips the Auth0 connection prefix ('auth0|' or
 * 'google-oauth2|') from the sub to create a consistent ID format.
 * For the authoritative user ID, use `fetchStudioUser()` which retrieves the database ID.
 *
 * @param auth0User - Auth0 user record.
 * @returns `User` record with sanitized Auth0 sub (without prefix).
 * @throws An error if the user record is invalid.
 */
export const toStudioUser = (auth0User: Auth0User): T_Maybe<User> => {
  const email = toString(auth0User.email);

  // Strip the Auth0 connection prefix from the sub. For a database login
  // ('auth0|' + hex id) this is the registry id fetchStudioUser() returns.
  // ENG-3451: a Google sub ('google-oauth2|' + Google id) is stripped the same
  // way, but its registry id is a server-side hash (GenProviderID), so this
  // id is provisional only; fetchStudioUser() never falls back to it.
  const rawSub = toString(auth0User.sub);
  const sanitizedSub = rawSub ? rawSub.replace(/^(auth0|google-oauth2)\|/, '').toLowerCase() : '';
  const id = normalizeId<User['id']>(sanitizedSub);

  const name = tokenize(toString(auth0User.name));
  const role = toRole(toStringArray(auth0User['iris/roles']).join(' ')) ?? Role.Operator;
  const timezone = toString(auth0User.tz, { default: UTC });

  const firstName = reduce(
    auth0User.given_name,
    name[0],
    auth0User.nickname,
    auth0User.preferred_username
  );

  const lastName = reduceOptional(auth0User.family_name, name.slice(1).join(' '));

  if (!id || !email || !firstName) {
    return undefined;
  }

  return {
    email,
    firstName,
    id,
    lastName,
    role,
    timezone
  };
};

/**
 * Extract organization data from Auth0 user app_metadata.
 *
 * @param auth0User - Auth0 user record.
 * @returns Organization data or undefined if not available.
 */
export const extractOrganizationData = (auth0User: Auth0User): T_Maybe<Auth0OrganizationData> => {
  try {
    const authNamespace = config.oauth.url.split('.')[0];
    const appMetadata = auth0User[`iris-${authNamespace}::app_metadata`];

    if (!appMetadata || typeof appMetadata !== 'object') {
      return undefined;
    }

    // Extract organization name - prefer 'name' over 'org_name'
    const organizationName = toString(appMetadata.name) || toString(appMetadata.org_name);

    // Extract organization industry
    const organizationIndustry =
      toString(appMetadata.industry) || toString(appMetadata.org_industry);

    // Validate organization name (basic sanitization)
    if (!organizationName || organizationName.trim().length === 0) {
      return undefined;
    }

    // Sanitize the organization name (remove excessive whitespace, limit length)
    const sanitizedName = organizationName.trim().slice(0, 100);
    const sanitizedIndustry = organizationIndustry?.trim().slice(0, 50);

    return {
      name: sanitizedName,
      ...(sanitizedIndustry && { industry: sanitizedIndustry })
    };
  } catch (error) {
    console.error('___', {
      message: 'Error extracting organization data from Auth0',
      error: error instanceof Error ? error.message : String(error)
    });
    return undefined;
  }
};
